import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Users, HeartHandshake, CheckCircle2, ArrowRight, Home } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import SEO from "@/components/site/SEO";
import { DecorationField, Star } from "@/components/site/Decorations";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { IMG } from "@/lib/siteData";
import { LEARNER_SUPPORT } from "@/lib/programData";

const approach = [
  {
    icon: Sparkles,
    title: "Individualized Instruction",
    color: "from-brand-green to-brand-green-deep",
    pill: "bg-brand-green-soft text-brand-green-deep",
    desc: "Lessons are adapted to each child's IEP goals through play, movement, and multi-sensory activities. Educators meet each learner where they are — pacing tasks, rotating modalities, and weaving in passions so growth feels joyful, not pressured.",
  },
  {
    icon: Users,
    title: "Collaborative Team Approach",
    color: "from-brand-purple to-brand-purple-deep",
    pill: "bg-brand-purple-soft text-brand-purple-deep",
    desc: "Teachers, therapists, and support staff meet regularly to review progress, align strategies, and celebrate wins. Every adult in a child's circle works from the same page — so consistency follows them from circle time, to therapy, to home.",
  },
  {
    icon: HeartHandshake,
    title: "Family Engagement",
    color: "from-brand-yellow to-amber-500",
    pill: "bg-brand-yellow/30 text-amber-800",
    desc: "Families are partners, not bystanders. We send frequent communication, share take-home learning resources, and host workshops so caregivers feel informed, equipped, and confident extending classroom growth into everyday moments at home.",
  },
  {
    icon: CheckCircle2,
    title: "Behavioral & Social-Emotional Support",
    color: "from-pink-500 to-rose-500",
    pill: "bg-rose-100 text-rose-700",
    desc: "We embed strategies that promote self-regulation, positive peer interactions, and emotional resilience. Through co-regulation, visual supports, and play-based coaching, children practice naming feelings and navigating challenges with calm, confident adults beside them.",
  },
];

export default function OurApproachPage() {
  return (
    <div data-testid="approach-page" className="overflow-hidden">
      <SEO
        title="Our Approach"
        description="Discover how Innovators Prep supports every learner through individualized instruction, collaborative teams, family engagement, and social-emotional support."
      />
      <PageHero title="Our Approach" crumb="Our Approach" />

      {/* Intro */}
      <section className="relative py-20 md:py-28">
        <DecorationField variant="mixed" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="inline-block px-3 py-1 rounded-full bg-brand-purple-soft text-brand-purple-deep text-xs font-bold tracking-widest uppercase">
              How We Teach
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              A whole-child framework, <span className="text-brand-green">delivered with care</span>
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed">
              Our approach is woven into every part of the day — from the way a teacher kneels to make
              eye contact, to the rhythm of small-group rotations, to the language we use when a
              child needs a moment to reset. Four core pillars guide us, and they show up in our
              private Preschool Program (ages 2–5) and our NYS-approved 4410 Special Education
              classrooms alike.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative">
              <div className="absolute -inset-5 rounded-[3rem] bg-brand-green-soft -rotate-2" />
              <img
                src={IMG.preschoolApproach}
                alt="Young girl exploring a science kit at preschool"
                className="relative rounded-[2.5rem] w-full aspect-[5/4] object-cover shadow-xl"
              />
              <div className="absolute -top-4 -right-3 animate-float">
                <Star color="#FBBF24" size={42} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pillars */}
      <section className="relative py-20 md:py-28 bg-brand-purple-soft/30">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="text-center max-w-2xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full bg-brand-green-soft text-brand-green-deep text-xs font-bold tracking-widest uppercase">
              The Four Pillars
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              What makes our classrooms <span className="text-brand-purple">work</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 gap-7">
            {approach.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.08}>
                <div
                  data-testid={`approach-pillar-${i}`}
                  className="group h-full bg-white rounded-[2rem] border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 p-8 md:p-10 relative overflow-hidden"
                >
                  <div className={`absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br ${a.color} opacity-10 group-hover:opacity-20 transition-opacity`} />
                  <div className="relative flex items-start gap-4">
                    <span className={`h-14 w-14 shrink-0 rounded-2xl bg-gradient-to-br ${a.color} text-white flex items-center justify-center shadow-md`}>
                      <a.icon className="h-6 w-6" />
                    </span>
                    <div>
                      <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${a.pill}`}>
                        Pillar {String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className="mt-2 font-display text-2xl font-bold text-gray-900">{a.title}</h3>
                    </div>
                  </div>
                  <p className="relative mt-5 text-[15.5px] text-gray-600 leading-relaxed">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How We Support Every Learner */}
      <section className="relative py-20 md:py-28" data-testid="learner-support-section">
        <DecorationField variant="mixed" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full bg-brand-yellow/25 text-amber-800 text-xs font-bold tracking-widest uppercase">
              Program by Program
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              How We Support <span className="text-brand-green">Every Learner</span>
            </h2>
            <p className="mt-4 text-gray-600 text-lg">
              Our four pillars come to life differently in each classroom. Explore how we tailor our
              approach to every age group and every child.
            </p>
          </Reveal>

          <Tabs defaultValue="2k" className="mt-12">
            <TabsList
              className="mx-auto flex flex-wrap h-auto justify-center gap-2 bg-white/80 backdrop-blur ring-1 ring-black/[0.05] rounded-full p-1.5 w-fit"
              data-testid="support-tabs"
            >
              {LEARNER_SUPPORT.map((s) => (
                <TabsTrigger
                  key={s.key}
                  value={s.key}
                  data-testid={`support-tab-${s.key}`}
                  className="rounded-full px-5 py-2 text-sm font-bold text-gray-600 hover:text-gray-900 data-[state=active]:bg-gradient-to-r data-[state=active]:from-brand-purple data-[state=active]:to-brand-purple-deep data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
                >
                  {s.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {LEARNER_SUPPORT.map((s) => (
              <TabsContent
                key={s.key}
                value={s.key}
                className="mt-12 focus-visible:ring-0"
                data-testid={`support-panel-${s.key}`}
              >
                <Reveal>
                  <div className="grid lg:grid-cols-5 gap-8 items-start">
                    {/* Left card with heading + intro */}
                    <div className={`lg:col-span-2 rounded-[2rem] p-8 bg-gradient-to-br ${s.color} text-white shadow-lg relative overflow-hidden`}>
                      <div aria-hidden className="absolute -bottom-10 -right-10 opacity-15">
                        <Star color="#fff" size={140} />
                      </div>
                      <span className="inline-block text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full bg-white/25">
                        {s.label}
                      </span>
                      <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold leading-tight">
                        {s.heading}
                      </h3>
                      <p className="mt-5 text-[15px] text-white/90 leading-relaxed">{s.intro}</p>
                    </div>

                    {/* Bullets + outro */}
                    <div className="lg:col-span-3">
                      <p className="text-gray-700 text-lg font-semibold">{s.listLabel}</p>
                      <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                        {s.items.map((item) => (
                          <li
                            key={item}
                            className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-gray-100 shadow-sm"
                          >
                            <span className={`h-7 w-7 shrink-0 rounded-full ${s.pill} flex items-center justify-center`}>
                              <CheckCircle2 className="h-4 w-4" />
                            </span>
                            <span className="text-[14.5px] text-gray-700 leading-snug">{item}</span>
                          </li>
                        ))}
                      </ul>
                      <div className={`mt-6 rounded-2xl p-5 ${s.pill}`}>
                        <p className="text-[15.5px] font-semibold leading-relaxed">{s.outro}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              </TabsContent>
            ))}
          </Tabs>

          {/* Partnering With Families */}
          <Reveal>
            <div
              data-testid="partnering-with-families"
              className="mt-16 rounded-[2.5rem] bg-gradient-to-br from-brand-green-soft via-white to-brand-purple-soft border border-white shadow-md p-8 md:p-12 flex flex-col md:flex-row gap-8 items-center text-center md:text-left"
            >
              <div className="h-20 w-20 shrink-0 rounded-3xl bg-gradient-to-br from-brand-green to-brand-purple text-white flex items-center justify-center shadow-md">
                <Home className="h-9 w-9" />
              </div>
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-brand-green text-white text-xs font-bold tracking-widest uppercase">
                  Home + School
                </span>
                <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold text-gray-900">
                  Partnering With Families
                </h3>
                <p className="mt-3 text-gray-700 leading-relaxed">
                  No matter which Innovators Prep program a child attends, families are an essential
                  part of our team. We encourage open communication and meaningful collaboration
                  between families, teachers, specialists, and school leadership. By working
                  together, we can better understand each child's strengths and needs, celebrate
                  developmental milestones, address concerns, and provide consistency between home
                  and school.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28">
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="rounded-[2.5rem] bg-gradient-to-br from-brand-purple-deep to-brand-green-deep text-white p-10 md:p-14 text-center shadow-xl relative overflow-hidden">
              <div aria-hidden className="absolute -top-12 -left-10 opacity-20">
                <Star color="#FBBF24" size={120} />
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold">
                See our approach in action
              </h2>
              <p className="mt-3 max-w-2xl mx-auto text-white/85">
                Schedule a visit — meet our teachers and see how these pillars come alive in every
                classroom moment.
              </p>
              <div className="mt-7 flex flex-wrap gap-3 justify-center">
                <Button
                  asChild
                  data-testid="approach-cta-contact"
                  className="bg-white text-brand-purple-deep hover:bg-brand-yellow hover:text-gray-900 rounded-full px-7 py-6 font-semibold"
                >
                  <Link to="/contact">Book a Tour <ArrowRight className="h-4 w-4 ml-1" /></Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full px-7 py-6 font-semibold bg-transparent border-2 border-white text-white hover:bg-white hover:text-brand-purple-deep"
                >
                  <Link to="/#programs">Explore Programs</Link>
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
